/* eslint-disable react/jsx-no-duplicate-props */
/* eslint-disable react/display-name */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MenuList, MenuItem } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import Header from '../../../components/Header';
import { Flex } from '../../../components/NavBar/styles';
import AddBtn from '../../../components/Botoes/AddBtn';
import { Body } from '../../../components/Body';
import MaterialTable, { Column } from 'material-table';
import { config } from '../../../components/Tabela';
import useStore from '../../../services/hooks/useStore';
import { useLocalStore, observer } from 'mobx-react-lite';
import { useHistory, useLocation } from 'react-router-dom';
import { apiV2 } from '../../../services/utils';
import Formatter from '../../../services/Formatter';
import { permissoes } from '../../../services/utils/permissoes';
import X9Logger from '../../../services/X9Logger';
import ActionButton from '../../../components/Tabela/ActionButton';
import Confirmar from '../../../components/Modais/Confirmar';
import useModal from '../../../services/hooks/useModal';
import { abrirDanfePreview } from '../../../components/DanfePreview';
import { isNfeSituacao } from '../../../services/utils/nfe';
import ModalNFe from '../../../components/Modais/ModalNFe'
import ModalNFeAvulsa from "../../../components/Modais/ModalNFEAvulsa";


type State = PageState & {
  sync: boolean;
  setPageSize(value: number): void;
  setPage(num: number): void;
};

type LocationStateNfe = {
  agendarAtualizacaoNfeId?: string | number;
};

/** Nome/razão social em `infNFe.dest` (tag xNome da NFe). */
function obterXnomeDestinatarioInfNFe(row: any): string {
  const j = row?.json_content ?? row?.jsonContent;
  const dest = j?.infNFe?.dest;
  if (!dest || typeof dest !== 'object') return '';
  const raw = (dest as Record<string, unknown>).xnome ?? (dest as Record<string, unknown>).xNome;
  const s = raw != null ? String(raw).trim() : '';
  return s;
}

/** Coluna: cadastro (`cliente_nome`) e nome no XML (`dest.xnome`) quando vier na listagem. */
function textoColunaClienteNomeDest(row: any): string {
  const cliente = String(row?.cliente_nome ?? '').trim();
  const nomeDest = obterXnomeDestinatarioInfNFe(row);
  if (cliente && nomeDest && cliente.toLowerCase() !== nomeDest.toLowerCase()) {
    return `${cliente} | ${nomeDest}`;
  }
  if (nomeDest) return nomeDest;
  if (cliente) return cliente;
  return '-';
}

const NFEEmitidas: React.FC = () => {
  const tableRef = useRef();
  const store = useStore();
  const history = useHistory();
  const location = useLocation();
  const timeoutsPosEmissaoRef = useRef<number[]>([]);
  const modalCancelar = useModal();
  const modalReenviar = useModal();
  const modalExcluir = useModal()
  const modalNFeAvulsa = useModal();
  const [selectedNFe, setSelectedNFe] = useState<any>(null);
  const [nfeTabelaSelecionada, setNfeTabelaSelecionada] = useState<any>(null);
  const [movimentacaoUuid, setMovimentacaoUuid] = useState<string | null>(null);
  const [justificativaCancelamento, setJustificativaCancelamento] = useState<string>('');
  const [erroJustificativa, setErroJustificativa] = useState<string | null>(null);
  const [tipoNfeAvulsa, setTipoNfeAvulsa] = useState<'ENTRADA' | 'CIAP'>('ENTRADA');

  const temPermissaoEscrita = store.checkPermissions(permissoes.NOTA_FISCAL_NFE_EMITIDAS_ESCRITA);

  const state = useLocalStore(
    (): State => ({
      pageSize: 0,
      setPageSize(value: number) {
        this.pageSize = value + 1;
      },
      setPage(num: number) {
        this.page = num;
      },
      page: 0,
      sync: false,
    }),
  );

  store.propriedade && store.validarPermissao(permissoes.NOTA_FISCAL_NFE_EMITIDAS_LEITURA, history);

  async function imprimirNFe(nfe: any) {
    try {
      store.toggleLoader();


      if (nfe.status.toLowerCase() === 'autorizada') {
        const endpoint =
          `/nfe/${nfe.id}/baixar_danfe/?prop=${store.propriedade?.id}`

        const response = await apiV2.get(endpoint, { responseType: 'blob' });
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
      } else {
        store.notificar('NFE não autorizada!');
      }

    } catch (error) {
      X9Logger.report(error as Error);
      store.notificar('Erro ao gerar impressão da NFe.');
    } finally {
      store.toggleLoader();
    }
  }

  async function baixarXmlNFe(nfe: any) {
    if (!isNfeSituacao(nfe.status, 'AUTORIZADA')) {
      store.notificar('NFE não autorizada!');
      return;
    }

    const endpoint = `/nfe/${nfe.id}/baixar_xml/`;
    try {
      store.toggleLoader();
      const response = await apiV2.get(endpoint, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/xml' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `nfe_${nfe.id}.xml`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      let msg = 'Erro ao baixar XML da NFe.';
      try {
        const resp = error?.response;
        if (resp?.data) {
          const data = resp.data;
          let parsed: any = null;

          if (data instanceof Blob) {
            const text = await data.text();
            try {
              parsed = JSON.parse(text);
            } catch {
              // se não for JSON, ignora
            }
          } else if (typeof data === 'string') {
            try {
              parsed = JSON.parse(data);
            } catch {
              // se não for JSON, usa a própria string como mensagem
              msg = data;
            }
          } else if (typeof data === 'object') {
            parsed = data;
          }

          if (parsed?.erro) {
            msg = parsed.erro;
          }
        }
      } catch {
        // qualquer problema de parse mantém a mensagem padrão
      }

      store.notificar(msg);
    } finally {
      store.toggleLoader();
    }
  }

  function validarJustificativa(valor: string): boolean {
    const texto = valor.trim();
    if (texto.length < 15) {
      setErroJustificativa('A justificativa deve conter no mínimo 15 caracteres.');
      return false;
    }
    if (texto.length > 255) {
      setErroJustificativa('A justificativa deve conter no máximo 255 caracteres.');
      return false;
    }
    setErroJustificativa(null);
    return true;
  }

  async function cancelarNFe() {
    if (!selectedNFe) return;
    if (!validarJustificativa(justificativaCancelamento)) {
      return;
    }
    try {
      store.toggleLoader();
      await apiV2.post(
        `/nfe/${selectedNFe.uuid}/cancelar/?prop=${store.propriedade?.id}`,
        { justification: justificativaCancelamento.trim() },
      );
      store.notificar('NFe cancelada com sucesso!');
      modalCancelar.close();
      setSelectedNFe(null);
      setJustificativaCancelamento('');
      (tableRef.current as any)?.onQueryChange();
    } catch (error: any) {
      X9Logger.report(error as Error);
      const msg = error.response?.data?.erros
        ? (Array.isArray(error.response.data.erros) ? error.response.data.erros.join(', ') : error.response.data.erros)
        : 'Erro ao cancelar NFe. Tente novamente.';
      store.notificar(msg);
    } finally {
      store.toggleLoader();
    }
  }

  function reenviarNFe(nfe: any) {
    const movId = nfe.movimentacao_id?.toString() || nfe.movimentacao?.uuid || nfe.movimentacao?.id?.toString();
    if (!movId) {
      store.notificar('Movimentação não encontrada para esta NFe.');
      return;
    }
    setMovimentacaoUuid(movId);
    modalReenviar.open();
  }

  async function excluirNFe() {
    if (!selectedNFe) return;
    try {
      store.toggleLoader();
      await apiV2.delete(`/nfe/${selectedNFe.id}/?prop=${store.propriedade?.id}`);
      store.notificar('NFe excluída com sucesso!');
      modalExcluir.close();
      setSelectedNFe(null);
      (tableRef.current as any)?.onQueryChange();
    } catch (error: any) {
      X9Logger.report(error as Error);
      const msg = error.response?.data?.erros
        ? (Array.isArray(error.response.data.erros) ? error.response.data.erros.join(', ') : error.response.data.erros)
        : 'Erro ao excluir NFe. Tente novamente.';
      store.notificar(msg);
    } finally {
      store.toggleLoader();
    }
  }

  const limparTimeoutsAtualizacaoPosEmissao = useCallback(() => {
    timeoutsPosEmissaoRef.current.forEach((t) => clearTimeout(t));
    timeoutsPosEmissaoRef.current = [];
  }, []);

  /** Após enviar NFe à SEFAZ: pede sync de status + recarrega a tabela aos 5s e aos 10s. */
  const agendarAtualizacaoStatusAposEmissaoNfe = useCallback(
    (nfeId: string | number) => {
      limparTimeoutsAtualizacaoPosEmissao();
      const id = String(nfeId);

      const sincronizarEAtualizarTabela = async () => {
        try {
          await apiV2.get(`/nfe/${id}/task_atualizar_status/`);
        } catch (error) {
          X9Logger.report(error as Error);
        } finally {
          (tableRef.current as any)?.onQueryChange?.();
        }
      };

      timeoutsPosEmissaoRef.current.push(
        window.setTimeout(() => {
          void sincronizarEAtualizarTabela();
        }, 5000),
        window.setTimeout(() => {
          void sincronizarEAtualizarTabela();
        }, 10000),
      );
    },
    [limparTimeoutsAtualizacaoPosEmissao],
  );

  async function atualizarStatusNFe(nfe: any) {
    try {
      store.toggleLoader();
      await apiV2.get(`/nfe/${nfe.id}/task_atualizar_status/`);
      store.notificar('Atualização de status da NFe iniciada. Atualizando em 5 segundos...');


      setTimeout(() => {
        (tableRef.current as any)?.onQueryChange();
      }, 5000);
    } catch (error: any) {
      X9Logger.report(error as Error);
      const msg =
        error?.response?.data?.erro ||
        (typeof error?.response?.data === 'string' ? error.response.data : null) ||
        'Erro ao atualizar status da NFe.';
      store.notificar(msg);
    } finally {
      store.toggleLoader();
    }
  }

  async function visualizarNFe(nfe: any) {
    try {
      store.toggleLoader();
      const response = await apiV2.get(`/nfe/${nfe.id}/?prop=${store.propriedade?.id}`);
      const data = response.data;

      if (data.json_content?.infNFe) {
        abrirDanfePreview(data.json_content.infNFe, nfe.id);
      } else {
        store.notificar('JSON da nota fiscal nao disponivel. Utilize a opcao Imprimir.');
      }
    } catch (error) {
      X9Logger.report(error as Error);
      store.notificar('Erro ao carregar dados da nota fiscal.');
    } finally {
      store.toggleLoader();
    }
  }

  const itemsColumns: Column<ItemNFe>[] = [
    {
      field: 'codigo_produto',
      title: 'Código',
      align: 'left',
      ...(true && ({ width: 120 } as object)),
      render: ({ codigo_produto }: any) => {
        return <p style={{ textAlign: 'left' }}>{codigo_produto || '-'}</p>;
      },
    },

    {
      field: 'created_at',
      title: 'Data Emissão',
      align: 'left',
      ...(true && ({ width: 120 } as object)),
      render: ({ created_at }: any) => {
        if (!created_at) return <p style={{ textAlign: 'left' }}>-</p>;
        const date = new Date(created_at);
        const formatted = `${('0' + date.getDate()).slice(-2)}/${('0' + (date.getMonth() + 1)).slice(-2)}/${date.getFullYear()}`;
        return <p style={{ textAlign: 'left' }}>{formatted}</p>;
      },
    },

    {
      field: 'descricao',
      title: 'Descrição',
      align: 'left',
      ...(true && ({ width: 250 } as object)),
      render: ({ descricao }: any) => {
        return <p style={{ textAlign: 'left' }}>{descricao || '-'}</p>;
      },
    },
    {
      field: 'ncm',
      title: 'NCM',
      align: 'left',
      ...(true && ({ width: 100 } as object)),
      render: ({ ncm }: any) => {
        return <p style={{ textAlign: 'left' }}>{ncm || '-'}</p>;
      },
    },
    {
      field: 'cfop',
      title: 'CFOP',
      align: 'left',
      ...(true && ({ width: 80 } as object)),
      render: ({ cfop }: any) => {
        return <p style={{ textAlign: 'left' }}>{cfop || '-'}</p>;
      },
    },
    {
      field: 'pIcsm',
      title: 'Aliq. ICMS',
      align: 'left',
      ...(true && ({ width: 60 } as object)),
      render: ({ pIcsm }: any) => {
        return <p style={{ textAlign: 'left' }}>{pIcsm || '-'}</p>;
      },
    },
    {
      field: 'vIcms',
      title: 'Vr ICMS',
      align: 'left',
      ...(true && ({ width: 60 } as object)),
      render: ({ vIcms }: any) => {
        return <p style={{ textAlign: 'left' }}>{vIcms || '-'}</p>;
      },
    },
    {
      field: 'unidade',
      title: 'UN',
      align: 'left',
      ...(true && ({ width: 60 } as object)),
      render: ({ unidade }: any) => {
        return <p style={{ textAlign: 'left' }}>{unidade || '-'}</p>;
      },
    },
    {
      field: 'quantidade',
      title: 'Quant',
      align: 'left',
      ...(true && ({ width: 100 } as object)),
      render: ({ quantidade }: any) => {
        return <p style={{ textAlign: 'left' }}>{Formatter.formatNumber(3, quantidade)}</p>;
      },
    },
    {
      field: 'valor_unitario',
      title: 'Vr. Unit (R$)',
      align: 'left',
      ...(true && ({ width: 120 } as object)),
      render: ({ valor_unitario }: any) => {
        return <p style={{ textAlign: 'left' }}>{Formatter.formatNumber(2, valor_unitario)}</p>;
      },
    },
    {
      field: 'valor_total',
      title: 'Vr. Total (R$)',
      align: 'left',
      ...(true && ({ width: 140 } as object)),
      render: ({ valor_total }: any) => {
        return <p style={{ textAlign: 'left' }}>{Formatter.formatNumber(2, valor_total)}</p>;
      },
    },
  ];

  const columns: Column<NFe>[] = [
    {
      field: 'acoes',
      title: 'Ações',
      disableClick: true,
      ...(true && ({ width: 80 } as object)),
      render: (rowData: any) => (
        <ActionButton
          imprimirNFe={() => imprimirNFe(rowData)}
          baixarXmlNFe={
            isNfeSituacao(rowData.status, 'AUTORIZADA') ? () => baixarXmlNFe(rowData) : undefined
          }
          atualizarStatusNFe={() => atualizarStatusNFe(rowData)}
          visualizarNFe={
            !isNfeSituacao(rowData.status, 'AUTORIZADA')
              ? () => visualizarNFe(rowData)
              : undefined
          }
          cancelarNFe={
            isNfeSituacao(rowData.status, 'AUTORIZADA') && temPermissaoEscrita
              ? () => {
                setSelectedNFe(rowData);
                modalCancelar.open();
              }
              : undefined
          }
          reenviarNFe={
            isNfeSituacao(rowData.status, 'PENDENTE') && temPermissaoEscrita
              ? () => reenviarNFe(rowData)
              : undefined
          }
          excluirNFe={
            (isNfeSituacao(rowData.status, 'PENDENTE') ||
              isNfeSituacao(rowData.status, 'REJEITADA') ||
              isNfeSituacao(rowData.status, 'ERRO')) &&
              temPermissaoEscrita
              ? () => {
                setSelectedNFe(rowData);
                modalExcluir.open();
              }
              : undefined
          }
        />
      ),
    },
    {
      field: 'numero_nota',
      title: 'N. NFe',
      type: 'numeric',
      ...(true && ({ width: 100 } as object)),
    },
    {
      field: 'serie',
      title: 'Série',
      type: 'numeric',
      ...(true && ({ width: 80 } as object)),
    },

    {
      field: 'data_emissao',
      title: 'Data Emissão',
      type: 'date',
      ...(true && ({ width: 120 } as object)),
      render: ({ data_emissao }: any) => {
        if (!data_emissao) return '-';
        const date = new Date(data_emissao);
        return `${('0' + date.getDate()).slice(-2)}/${('0' + (date.getMonth() + 1)).slice(-2)}/${date.getFullYear()}`;
      },
    },
    {
      field: 'status',
      title: 'Situação',
      ...(true && ({ width: 140 } as object)),
      render: (rowData: any) => {
        const situacao = rowData.status || '';
        if (isNfeSituacao(situacao, 'AUTORIZADA')) {
          return <b style={{ color: 'green' }}>Autorizada</b>;
        } else if (isNfeSituacao(situacao, 'REJEITADA')) {
          return <b style={{ color: 'red' }}>Rejeitada</b>;
        } else if (isNfeSituacao(situacao, 'CANCELADA')) {
          return <b style={{ color: '#9e9e9e' }}>Cancelada</b>;
        } else if (isNfeSituacao(situacao, 'EM_PROCESSO')) {
          return <b style={{ color: '#ff9800' }}>Em Processo</b>;
        } else if (isNfeSituacao(situacao, 'PENDENTE')) {
          return <b style={{ color: '#2196f3' }}>Pendente</b>;
        } else if (isNfeSituacao(situacao, 'ERRO')) {
          return <b style={{ color: '#d32f2f' }}>Erro</b>;
        } else if (situacao) {
          return <b>{situacao.charAt(0).toUpperCase() + situacao.slice(1).toLowerCase()}</b>;
        } else {
          return <span>-</span>;
        }
      },
    },
    {
      field: 'data_autorizacao',
      title: 'Data Autorização',
      type: 'date',
      ...(true && ({ width: 140 } as object)),
      render: ({ data_autorizacao }: any) => {
        if (!data_autorizacao) return '-';
        const date = new Date(data_autorizacao);
        return `${('0' + date.getDate()).slice(-2)}/${('0' + (date.getMonth() + 1)).slice(-2)}/${date.getFullYear()}`;
      },
    },
    {
      field: 'cliente_nome',
      title: 'Cliente / Nome',
      ...(true && ({ width: 280 } as object)),
      render: (rowData: any) => {
        return <span style={{ wordBreak: 'break-word' }}>{textoColunaClienteNomeDest(rowData)}</span>;
      },
    },
    {
      field: 'valor_total',
      title: 'Vr Total (R$)',
      type: 'numeric',
      ...(true && ({ width: 140 } as object)),
      render: ({ valor_total }: any) => {
        return Formatter.formatNumber(2, valor_total);
      },
    },
    {
      field: 'chave_acesso',
      title: 'Chave',
      ...(true && ({ width: 300 } as object)),
      render: ({ chave_acesso }: any) => {
        return chave_acesso ? (
          <p style={{ fontSize: '12px', wordBreak: 'break-all' }}>{chave_acesso}</p>
        ) : (
          '-'
        );
      },
    },
    {
      field: 'complemento',
      title: 'Informações',
      ...(true && ({ width: 200 } as object)),
      render: ({ complemento }: any) => {
        return complemento ? (
          <p style={{ fontSize: '12px', wordBreak: 'break-word' }}>
            {complemento.length > 50 ? `${complemento.substring(0, 50)}...` : complemento}
          </p>
        ) : (
          '-'
        );
      },
    },
  ];

  async function sync(): Promise<void> {
    state.sync = true;
  }

  useEffect(() => store.setTitulo('Emissão NFE - Listagem'), []);

  useEffect(() => () => limparTimeoutsAtualizacaoPosEmissao(), []);

  useEffect(() => {
    const st = location.state as LocationStateNfe | null | undefined;
    const id = st?.agendarAtualizacaoNfeId;
    if (id == null || id === '') return;

    (tableRef.current as any)?.onQueryChange?.();
    agendarAtualizacaoStatusAposEmissaoNfe(id);

    history.replace({
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
      state: undefined,
    });
  }, [
    location.state,
    location.pathname,
    location.search,
    location.hash,
    history,
    agendarAtualizacaoStatusAposEmissaoNfe,
  ]);

  useEffect(() => {
    if (store.propriedade) {
      sync();
      (tableRef.current as any)?.onQueryChange();
    }

  }, [store.propriedade]);

  return state.sync ? (
    <>
      <Header>
        <Flex>
          <AddBtn
            disabled={!temPermissaoEscrita}
            dropdown
            onClick={() => {
              setTipoNfeAvulsa('ENTRADA');
              modalNFeAvulsa.open();
            }}
            text="ADICIONAR NFE AVULSA"
            Icon={AddIcon}
          >
            <MenuList>
              <MenuItem
                onClick={() => {
                  setTipoNfeAvulsa('ENTRADA');
                  modalNFeAvulsa.open();
                }}
              >
                NFE Entrada
              </MenuItem>
              <MenuItem disabled>NFE CIAP</MenuItem>
            </MenuList>
          </AddBtn>
        </Flex>
      </Header>

      <ModalNFeAvulsa
        open={modalNFeAvulsa.state}
        handleClose={modalNFeAvulsa.close}
        data={nfeTabelaSelecionada || undefined}
        tipo={tipoNfeAvulsa}
        onEnvioEmissaoSuccess={(notaFiscalId) => {
          (tableRef.current as any)?.onQueryChange?.();
          agendarAtualizacaoStatusAposEmissaoNfe(notaFiscalId);
        }}
      />

      <Body gridColumns="1fr">
        <div style={{ overflowX: 'hidden', borderRadius: '10px' }}>
          <MaterialTable
            tableRef={tableRef}
            columns={columns}
            data={(query) =>
              new Promise((resolve) => {

                try {
                  store.propriedade &&
                    apiV2
                      .get('/nfe/', {
                        params: {
                          page_size: 50,
                          page: query.page + 1,
                          prop: store.propriedade!.id,
                        },
                        headers: {
                          Authorization: `${store.token}`,
                        },
                      })
                      .then((res) => {
                        if (res.data.count) {
                          if (res.data.count >= 50) {
                            state.setPageSize(50);
                          } else {
                            state.setPageSize(res.data.count);
                          }
                        } else {
                          state.setPageSize(0);
                        }

                        resolve({
                          data: res.data.results || [],
                          page: query.page,
                          totalCount: res.data.count || 0,
                        });
                      });
                } catch (err) {
                  X9Logger.report(err as Error);
                  store.notificar('Ocorreu um erro ao adquirir informações. Tente novamente mais tarde!');
                  resolve({
                    data: [],
                    page: query.page,
                    totalCount: 0,
                  });
                }
              })
            }
            options={{ ...config.options, pageSize: 50, maxBodyHeight: window.innerHeight - 275, sorting: false }}
            localization={config.localization}
            style={config.style}
            onRowClick={(_evt, rowData: any) => {
              if (!rowData) return;
              setNfeTabelaSelecionada({
                numeroNota: rowData.numero_nota,
                serie: rowData.serie,
              });
            }}
            detailPanel={(rowData) => {

              return <ItemsTable columns={itemsColumns} items={rowData.itens || []} />;
            }}
          />
        </div>
      </Body>

      {modalCancelar.state && selectedNFe && (
        <Confirmar
          open={modalCancelar.state}
          content={
            <div>
              <p>
                Tem certeza que deseja cancelar a NFe <b>N° {selectedNFe.numero_nota}</b>?
              </p>
              <p style={{ marginTop: '10px' }}>
                <b>Cliente:</b> {selectedNFe.cliente_nome || '-'}
              </p>
              <p>
                <b>Valor:</b> R$ {Formatter.formatNumber(2, selectedNFe.valor_total)}
              </p>
              <p>
                <b>Chave:</b> {selectedNFe.chave_acesso || '-'}
              </p>
              <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column' }}>
                <label htmlFor="justificativa-cancelamento" style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                  Motivo / Justificativa
                </label>
                <textarea
                  id="justificativa-cancelamento"
                  value={justificativaCancelamento}
                  onChange={(e) => {
                    setJustificativaCancelamento(e.target.value);
                    if (erroJustificativa) {
                      validarJustificativa(e.target.value);
                    }
                  }}
                  style={{ width: '100%', minHeight: '80px', resize: 'vertical', padding: '12px', boxSizing: 'border-box' }}
                  maxLength={255}
                />
                <small style={{ marginTop: '4px', color: '#666' }}>
                  Mínimo de 15 e máximo de 255 caracteres.
                </small>
                {erroJustificativa && (
                  <span style={{ marginTop: '4px', color: 'red', fontSize: '12px' }}>{erroJustificativa}</span>
                )}
              </div>
            </div>
          }
          title="Cancelar Nota Fiscal"
          handleClose={() => {
            modalCancelar.close();
            setSelectedNFe(null);
            setJustificativaCancelamento('');
            setErroJustificativa(null);
          }}
          onCancel={() => {
            modalCancelar.close();
            setSelectedNFe(null);
            setJustificativaCancelamento('');
            setErroJustificativa(null);
          }}
          onConfirm={cancelarNFe}
        />
      )}

      {modalExcluir.state && selectedNFe && (
        <Confirmar
          open={modalExcluir.state}
          content={
            <div>
              <p style={{ marginBottom: '12px' }}>Tem certeza que deseja excluir a NFe abaixo?</p>
              <p><b>N. NF:</b> {selectedNFe.numero_nota || '-'}</p>
              <p><b>Série:</b> {selectedNFe.serie || '-'}</p>
              <p><b>Data Emissão:</b> {selectedNFe.data_emissao ? (() => { const d = new Date(selectedNFe.data_emissao); return `${('0' + d.getDate()).slice(-2)}/${('0' + (d.getMonth() + 1)).slice(-2)}/${d.getFullYear()}`; })() : '-'}</p>
              <p><b>Situação:</b> {selectedNFe.status_display || selectedNFe.status || '-'}</p>
              <p><b>Cliente:</b> {selectedNFe.cliente_nome || '-'}</p>
              <p><b>Valor R$:</b> {Formatter.formatNumber(2, selectedNFe.valor_total)}</p>
            </div>
          }
          title="Excluir Nota Fiscal"
          handleClose={() => {
            modalExcluir.close();
            setSelectedNFe(null);
          }}
          onCancel={() => {
            modalExcluir.close();
            setSelectedNFe(null);
          }}
          onConfirm={excluirNFe}
        />
      )}

      {modalReenviar.state && movimentacaoUuid && (
        <ModalNFe
          open={modalReenviar.state}
          handleClose={() => {
            modalReenviar.close();
            setMovimentacaoUuid(null);
          }}
          preEmissaoOrigem="movimentacao"
          preEmissaoContextId={movimentacaoUuid}
          onSuccess={(meta) => {
            modalReenviar.close();
            setMovimentacaoUuid(null);
            (tableRef.current as any)?.onQueryChange();
            if (meta?.notaFiscalId) {
              agendarAtualizacaoStatusAposEmissaoNfe(meta.notaFiscalId);
            }
          }}
        />
      )}
    </>
  ) : null;
};

export default observer(NFEEmitidas);

type ItemTableProps = {
  items: ItemNFe[];
  columns: Column<ItemNFe>[];
};

const ItemsTable: React.FC<ItemTableProps> = ({ items, columns }) => {
  return (
    <MaterialTable
      columns={columns}
      data={items}
      options={{
        ...config.options,
        paging: false,
        minBodyHeight: 0,
        maxBodyHeight: 600,
        pageSize: items.length + 1,
        headerStyle: {
          background: 'white',
          color: '#2D2D2D',
          height: '40px',
          paddingTop: 10,
          paddingBottom: 10,
          fontSize: 14,
          borderBottom: 'none',
          zIndex: 0,
        },
      }}
      localization={config.localization}
      style={config.style}
    />
  );
};

